import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowCounterClockwiseIcon, TrashIcon, CheckCircleIcon } from "@phosphor-icons/react";

export default function Trash() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/cars", { params: { deleted: true } });
      setCars(r.data);
    } catch {
      toast.error("Αποτυχία φόρτωσης διαγραμμένων");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onRestore = async (id) => {
    try {
      await api.post(`/cars/${id}/restore`);
      toast.success("Επαναφέρθηκε στον στόλο");
      setCars((cs) => cs.filter((c) => c.id !== id));
    } catch {
      toast.error("Αποτυχία επαναφοράς");
    }
  };

  const onMarkSold = async (id) => {
    try {
      await api.post(`/cars/${id}/restore?mark_sold=1`);
      toast.success("Μεταφέρθηκε στα πωλημένα");
      setCars((cs) => cs.filter((c) => c.id !== id));
    } catch {
      toast.error("Αποτυχία");
    }
  };

  const onPermanentDelete = async (id, label) => {
    if (!window.confirm(`Οριστική διαγραφή του "${label}";\n\nΑυτή η ενέργεια ΔΕΝ μπορεί να αναιρεθεί. Θα διαγραφούν και οι φωτογραφίες από τον δίσκο.`)) return;
    try {
      await api.delete(`/cars/${id}/permanent`);
      toast.success("Διαγράφηκε οριστικά");
      setCars((cs) => cs.filter((c) => c.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία διαγραφής");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("el-GR", { dateStyle: "short", timeStyle: "short" });
    } catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="trash-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="heading text-4xl font-bold">Διαγραμμένα</h2>
          <p className="text-sm text-[#4B5563] mt-1">
            Διαγραμμένα αυτοκίνητα παραμένουν εδώ για επαναφορά. Η οριστική διαγραφή είναι μη αναστρέψιμη.
          </p>
        </div>
      </div>

      {loading && <div className="p-12 text-center text-[#4B5563]">Φόρτωση...</div>}

      {!loading && cars.length === 0 && (
        <div className="bg-white border border-black/10 p-12 text-center" data-testid="trash-empty">
          <TrashIcon size={40} className="mx-auto text-[#9CA3AF] mb-3" />
          <div className="text-[#4B5563]">Δεν υπάρχουν διαγραμμένα αυτοκίνητα.</div>
        </div>
      )}

      {!loading && cars.length > 0 && (
        <div className="bg-white border border-black/10">
          <table className="w-full text-sm">
            <thead className="border-b border-black/10 bg-[#F9FAFB]">
              <tr className="text-left">
                <th className="px-4 py-3 text-xs uppercase tracking-wider text-[#6B7280]">Αυτοκίνητο</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wider text-[#6B7280]">Έτος</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wider text-[#6B7280]">Τιμή</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wider text-[#6B7280]">Διαγράφηκε</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wider text-[#6B7280] text-right">Ενέργειες</th>
              </tr>
            </thead>
            <tbody>
              {cars.map((c) => (
                <tr key={c.id} className="border-b border-black/5 last:border-b-0" data-testid={`trash-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{c.brand} {c.model}</div>
                    {c.variant && <div className="text-xs text-[#6B7280]">{c.variant}</div>}
                  </td>
                  <td className="px-4 py-3 text-[#4B5563]">{c.year || "—"}</td>
                  <td className="px-4 py-3 text-[#4B5563]">
                    {c.price_eur ? `${Number(c.price_eur).toLocaleString("el-GR")} €` : "—"}
                  </td>
                  <td className="px-4 py-3 text-[#6B7280] text-xs">{fmtDate(c.deleted_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <button
                        onClick={() => onRestore(c.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#0055FF] text-[#0055FF] text-xs font-medium hover:bg-[#0055FF] hover:text-white transition-colors"
                        data-testid={`restore-${c.id}`}
                      >
                        <ArrowCounterClockwiseIcon size={14} /> Επαναφορά
                      </button>
                      <button
                        onClick={() => onMarkSold(c.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#16A34A] text-[#16A34A] text-xs font-medium hover:bg-[#16A34A] hover:text-white transition-colors"
                        data-testid={`mark-sold-${c.id}`}
                      >
                        <CheckCircleIcon size={14} /> Πουλήθηκε
                      </button>
                      <button
                        onClick={() => onPermanentDelete(c.id, `${c.brand} ${c.model}`)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#E60000] text-[#E60000] text-xs font-medium hover:bg-[#E60000] hover:text-white transition-colors"
                        data-testid={`permanent-delete-${c.id}`}
                      >
                        <TrashIcon size={14} /> Οριστική διαγραφή
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
